// Live parking hints during a ride (Figma 06 / E1 / E3): which zone the bike is in and the nearest
// parkings. Pure, client-side, from GET /api/parking; Pony's START_FINISHING verdict stays the authority.
import type { ParkingResponse, ParkingZone, Position } from "./api";

/** Ray casting on a [lng, lat] ring, closed or open. */
export function inPolygon(p: Position, ring: [number, number][]): boolean {
  if (ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i] as [number, number];
    const [xj, yj] = ring[j] as [number, number];
    if (yi > p.lat !== yj > p.lat && p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
}

export type ZoneStatus =
  | { kind: "inside"; zone: ParkingZone }
  | { kind: "forbidden" }
  | { kind: "outside" }
  /** No parking zone has an area (GBFS without station_area): no hint at all. */
  | { kind: "unknown" };

export function zoneStatus(p: Position, data: ParkingResponse): ZoneStatus {
  if (data.forbidden.some((z) => inPolygon(p, z.polygon))) return { kind: "forbidden" };
  const withArea = data.parking.filter((z) => z.polygon && z.polygon.length >= 3);
  if (withArea.length === 0) return { kind: "unknown" };
  const zone = withArea.find((z) => inPolygon(p, z.polygon as [number, number][]));
  return zone ? { kind: "inside", zone } : { kind: "outside" };
}

export function metersBetween(a: Position, b: Position): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const x =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.sqrt(x));
}

export interface NearParking {
  zone: ParkingZone;
  meters: number;
}

/** The `n` parkings closest to `p` (by their point), nearest first. */
export function nearestParkings(p: Position, zones: ParkingZone[], n: number): NearParking[] {
  return zones
    .map((zone) => ({ zone, meters: metersBetween(p, zone) }))
    .sort((a, b) => a.meters - b.meters)
    .slice(0, n);
}

/** Minutes by bike at ~15 km/h (250 m/min), at least one. */
export const bikeMinutes = (meters: number) => Math.max(1, Math.ceil(meters / 250));

/** "120 m · 1 min", "1,2 km · 5 min" (by bike). */
export function rideDistanceLabel(meters: number): string {
  const m = Math.round(meters / 10) * 10;
  const d = m >= 1000 ? `${(m / 1000).toFixed(1).replace(".", ",")} km` : `${m} m`;
  return `${d} · ${bikeMinutes(meters)} min`;
}
